import type React from 'react';
import { css } from 'styled-components';

export const FIELD_CONTROL_PADDING =
  'var(--puck-field-space-y, var(--puck-space-3, 12px)) var(--puck-field-space-x, calc(var(--puck-space-4, 16px) - var(--puck-field-border-width, 1px)))';

export const FIELD_CONTROL_RADIUS = 'var(--dcm-cms-select-radius, var(--puck-field-radius, var(--puck-radius-m, 6px)))';

export const FIELD_CONTROL_BORDER_COLOR =
  'var(--dcm-cms-select-border-color, var(--puck-field-color-border, var(--puck-color-border, #d0d4d8)))';

export const FIELD_CONTROL_BORDER_WIDTH = 'var(--puck-field-border-width, var(--puck-border-width-regular, 1px))';

export const FIELD_CONTROL_FONT_SIZE = 'var(--puck-field-font-size, var(--puck-font-size-xxs, 14px))';

// Match the native Puck fields' inherited normalize line height, not the browser's control-specific `normal`.
export const FIELD_CONTROL_LINE_HEIGHT = 'var(--puck-field-line-height, 1.15)';

// A minimum preserves the baseline size without capping theme-specific font sizes or padding.
export const FIELD_CONTROL_HEIGHT = `calc(${FIELD_CONTROL_FONT_SIZE} * ${FIELD_CONTROL_LINE_HEIGHT} + 2 * var(--puck-field-space-y, var(--puck-space-3, 12px)) + 2 * ${FIELD_CONTROL_BORDER_WIDTH})`;

export const FIELD_CONTROL_BG = 'var(--dcm-cms-select-bg, var(--puck-field-color-bg, #fff))';

export const FIELD_CONTROL_TEXT_COLOR =
  'var(--dcm-cms-select-text-color, var(--puck-field-color-text, var(--puck-color-text, #121516)))';

/**
 * Shared CSSProperties for single-line inputs, selects, and text controls in Web Studio inspectors.
 * Matches Puck's native field dimensions and StyledSelect in `select-field.tsx`.
 */
export const fieldControlStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  fontFamily: 'var(--dcm-cms-select-font-family, inherit)',
  fontSize: FIELD_CONTROL_FONT_SIZE,
  lineHeight: FIELD_CONTROL_LINE_HEIGHT,
  padding: FIELD_CONTROL_PADDING,
  borderRadius: FIELD_CONTROL_RADIUS,
  borderWidth: FIELD_CONTROL_BORDER_WIDTH,
  borderStyle: 'solid',
  borderColor: FIELD_CONTROL_BORDER_COLOR,
  background: FIELD_CONTROL_BG,
  color: FIELD_CONTROL_TEXT_COLOR,
  transition: 'border-color var(--puck-duration-fast, 100ms) var(--puck-ease-exit, ease-out)',
};

/**
 * Shared CSSProperties for native <select> controls in Web Studio inspectors.
 */
export const selectControlStyle: React.CSSProperties = {
  ...fieldControlStyle,
  minHeight: FIELD_CONTROL_HEIGHT,
  appearance: 'none',
  // Puck uses a filled 18px chevron; gradients let this arrow inherit the field text color.
  backgroundImage:
    'linear-gradient(45deg, transparent 50%, currentColor 50%), linear-gradient(135deg, currentColor 50%, transparent 50%)',
  backgroundPosition: 'right 21px center, right 16.5px center',
  backgroundSize: '4.5px 4.5px',
  backgroundRepeat: 'no-repeat',
  paddingRight: 'max(var(--puck-field-space-x, 15px), 36px)',
  cursor: 'pointer',
};

/** Keep inline selects' text and currentColor chevrons muted when unavailable. */
export const disabledSelectControlStyle: React.CSSProperties = {
  color:
    'var(--dcm-cms-select-disabled-text-color, var(--puck-field-color-text-disabled, var(--puck-color-text-secondary, #646d72)))',
  cursor: 'default',
};

/**
 * Shared styled-components CSS fragment for inspector inputs and dropdowns.
 */
export const fieldControlCss = css`
  ${fieldControlStyle}

  &:focus {
    border-color: var(
      --dcm-cms-select-focus-border-color,
      var(--puck-field-color-border-hover, var(--puck-color-border-hover, #0b99e6))
    );
    outline: var(--puck-border-width-focus, 2px) solid
      var(
        --dcm-cms-select-focus-ring-color,
        var(--puck-field-color-border-focus, var(--puck-color-focus-ring, #0b99e6))
      );
  }

  &:disabled {
    background-color: var(
      --dcm-cms-select-disabled-bg,
      var(--puck-field-color-bg-disabled, var(--puck-color-surface-muted, #f4efe4))
    );
    border-color: var(
      --dcm-cms-select-disabled-border-color,
      var(--puck-field-color-border-disabled, var(--puck-color-border, #d0d4d8))
    );
    color: var(
      --dcm-cms-select-disabled-text-color,
      var(--puck-field-color-text-disabled, var(--puck-color-text-secondary, #646d72))
    );
    cursor: default;
    opacity: 1;
    outline: 0;
  }
`;
